import React, { FC, useCallback, useEffect, useMemo } from 'react';

import { observer } from 'mobx-react-lite';

import { EvFilters, IFilterValues, TFilterOptions } from '@evinced-private/ui-common';

import { PROPERTIES_PAGE_TABLE_ID } from '../../../../components/properties-table/PropertiesTableV2';
import { useCollections } from '../../../../providers/collections/CollectionsProvider';
import { useCommonFiltersStore } from '../../../../providers/commonFiltersProvider/CommonFiltersProvider';
import { useConfiguration } from '../../../../providers/configurationProvider/ConfigurationProvider';
import { useTablesStore } from '../../../../providers/tablesProvider/TablesProvider';
import { SiteScannerUiToggles } from '../../../../services/LocalTogglesService';
import {
	getFilterDefinitions,
	getInitialFilterValues,
	mapCollectionsToOptions,
	PROPERTIES_PAGE_FILTERS_ID
} from '../../helpers/PropertiesPageFiltersHelper';

interface IPropertiesPageFiltersProps {
	handleResetFilters: () => void;
	resetKey?: string | number;
}

const PropertiesPageFiltersRaw: FC<IPropertiesPageFiltersProps> = ({
	handleResetFilters,
	resetKey
}) => {
	const commonFiltersStore = useCommonFiltersStore();
	const tablesStore = useTablesStore();
	const { propertyCollections, isCollectionsLoading } = useCollections();
	const { getToggle } = useConfiguration();

	const isMultipleCollectionsEnabled = getToggle(SiteScannerUiToggles.ENABLE_MULTIPLE_COLLECTIONS);
	const { sizePerPage } = tablesStore.getPagination(PROPERTIES_PAGE_TABLE_ID);
	const appliedFilters = commonFiltersStore.getFilterValue(PROPERTIES_PAGE_FILTERS_ID);

	const filterValues = useMemo(
		() => getInitialFilterValues({ appliedFilters, propertyCollections, isCollectionsLoading }),
		[appliedFilters, propertyCollections, isCollectionsLoading]
	);

	const filterOptions: TFilterOptions = useMemo(
		() => ({
			// condition should be removed when multiple collections feature is fully integrated
			collections: isMultipleCollectionsEnabled ? mapCollectionsToOptions(propertyCollections) : []
		}),
		[propertyCollections, isMultipleCollectionsEnabled]
	);

	const filterDefinitions = useMemo(
		() => getFilterDefinitions(isMultipleCollectionsEnabled),
		[isMultipleCollectionsEnabled]
	);

	const handleApplyFilters = useCallback(
		(appliedFilters: IFilterValues): void => {
			const flatFilters = { ...appliedFilters.text, ...appliedFilters.select };
			commonFiltersStore.updateSelectedFilters(PROPERTIES_PAGE_FILTERS_ID, flatFilters);
			tablesStore.updatePagination(PROPERTIES_PAGE_TABLE_ID, { page: 1, sizePerPage });
		},
		[commonFiltersStore, sizePerPage, tablesStore]
	);

	// Aligning the store values with the filter values after removing non-existent collections
	useEffect(() => {
		if (!isCollectionsLoading) {
			commonFiltersStore.updateSelectedFilters(PROPERTIES_PAGE_FILTERS_ID, {
				...filterValues.text,
				...filterValues.select
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [commonFiltersStore, isCollectionsLoading]);

	return (
		<div>
			<EvFilters
				key={resetKey}
				onApply={handleApplyFilters}
				onReset={handleResetFilters}
				initialValues={filterValues}
				filterOptions={filterOptions}
				filterDefinitions={filterDefinitions}
				isFilterOptionsLoaded={!isCollectionsLoading}
				isInitialValuesLoaded={!isCollectionsLoading}
				align="END"
			/>
		</div>
	);
};

export const PropertiesPageFilters = observer(PropertiesPageFiltersRaw);
